import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { SelectOption } from '../../../../shared/models/select-option'; // Adjust the import path as necessary
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { Seismograph } from '../../../../shared/models/seismograph';
import { NewDeviceModalComponent } from '../add-device-modal/add-device-modal.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FonctionsService } from '../../../../shared/services/utilities/fonctions.service';
import { SeismographService } from '../../services/seismograph-service';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ControlPoint } from '@shared/models/controlPoint';
import { ControlPointsService } from '@features/control-points/services/control-points-service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Site } from '@shared/models/site';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { CurrentStateService } from 'app/core/services/current-state.service';
import { MENU_OPTIONS } from '@shared/constants/left-menu-option';
import { LocalStorageService } from '@shared/services/utilities/local-storage.service';
import { Location } from '@angular/common';
import { ReportControlPointSelectionModalComponent, ReportGenerationParams } from '@features/report/report-control-point-selection-modal/report-control-point-selection-modal.component';
import { ControlPointInfoDto, ReportService } from '@shared/services/report/report.service';

@Component({
  selector: 'app-device-detail',
  imports: [TranslateModule, MatIconModule, CommonModule, AutocompleteComponent, AngularSvgIconModule],
  templateUrl: './device-detail.component.html',
  styleUrl: './device-detail.component.scss',
})
export class DeviceDetailComponent {
  companyName: string = '';

  constructor(
    private router: Router,
    private refMatDiag: MatDialog,
    private cdRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<DeviceDetailComponent>,
    private fonctionsService: FonctionsService,
    private dialogConfirme: MatDialog,
    private seismographService: SeismographService,
    private controlPointService: ControlPointsService,
    private translate: TranslateService,
    protected currentStateService: CurrentStateService,
    private localStorageService: LocalStorageService,
    private location: Location,
    private reportService: ReportService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  @Input() device: Seismograph;
  @Input() siteId: number;
  @Output() navigateToVibrationDetail = new EventEmitter<{ siteId: number, controlPointId: number }>();

  showCurrentCtrlPoint = true;
  ctrlPoint: ControlPoint = null;
  isEditing = false;
  selectedControlPoint: ControlPoint | null = null;
  optionsControlPoint: SelectOption[] = [];
  edidtingCp = false;
  isControlPointSelected = false;
  isLoading = false;
  controlPoints: ControlPoint[] = [];
  allSites: Site[] = [];
  optionsSites: SelectOption[] = [];
  selectedSite: Site | null = null;

  get controlPointSite(): Site | null {
    if (this.ctrlPoint && this.ctrlPoint.siteId && this.allSites.length > 0) {
      return this.allSites.find(site => site.siteId === this.ctrlPoint.siteId) || null;
    }
    return null;
  }

  async ngOnInit(): Promise<void> {
  await this.setCompanyNameForDevice();
    if (!this.siteId) {
      this.siteId = this.localStorageService.getItem('currentSiteID');
    }
    
    this.allSites = this.localStorageService.getItem('allSites') || [];
    this.optionsSites = this.allSites.map(site => ({
      id: site.siteId,
      name: site.nom
    }));
    this.selectedSite = this.allSites.find(site => site.siteId === this.siteId) || null;
    if (this.selectedSite) {
      await this.loadAvailableControlPointsForSite(this.siteId);
    } else {
      this.controlPoints = [];
      this.optionsControlPoint = [];
    }
    
    this.loadControlPoint();
  }

  back() {
    this.location.back();
  }

  onModifyDevice() {
    console.log(`New device`);
    this.refMatDiag.open(NewDeviceModalComponent,
      {
        data: { device: this.device, isEdit: true },
        enterAnimationDuration: '0ms',
      }
    );

    this.refMatDiag.afterAllClosed.subscribe(async () => {
      this.cdRef.detectChanges();
    })
  }

  async openReportModalForCurrentDevice(): Promise<void> {
    let selectedControlPointId: number | null = this.ctrlPoint?.controlPointId ?? null;

    if (!selectedControlPointId && this.device?.id) {
      try {
        const controlPointInstrument = await this.seismographService.getControlPointInstrumentsBySeismographsId([this.device.id]);
        selectedControlPointId = controlPointInstrument?.[0]?.controlPointId ?? null;
      } catch (error) {
        selectedControlPointId = null;
      }
    }

    const dialogRef = this.dialogConfirme.open(ReportControlPointSelectionModalComponent, {
      width: '450px',
      disableClose: true,
      autoFocus: false,
      data: {
        siteId: this.siteId,
        controlPoints: this.controlPoints || [],
        devices: this.device ? [this.device] : [],
        selectedDeviceIds: this.device?.id ? [this.device.id] : [],
        selectedDeviceSerialNumbers: this.device?.serialNumber ? [this.device.serialNumber] : [],
        lockDevicesSelection: true,
        selectedControlPointIds: selectedControlPointId ? [selectedControlPointId] : [],
        lockControlPointsSelection: false,
        eventId: undefined,
        eventName: undefined,
        eventType: undefined,
        location: undefined,
        organisation: undefined
      }
    });

    dialogRef.afterClosed().subscribe((params: ReportGenerationParams) => {
      if (params) {
        this.generateMultiRecordReport(params);
      }
    });
  }

  private generateMultiRecordReport(params: ReportGenerationParams): void {
    const controlPointsDto: ControlPointInfoDto[] = params.controlPoints.map(cp => ({
      ControlPointId: cp.ControlPointId,
      ControlPointName: cp.ControlPointName
    }));

    this.currentStateService.setReportGenerationInProgress(true);
    this.reportService.generateVibrationReport(
      undefined,
      params.startDate,
      params.endDate,
      params.ponderationId,
      params.eventName,
      params.eventType,
      this.selectedSite?.nom,
      params.organisation,
      controlPointsDto,
      this.siteId,
      null,
      params.devices || null
    ).subscribe({
      next: (blob) => {
        this.downloadPDF(blob, `Report_Device_${this.device?.serialNumber || ''}_${new Date().toLocaleDateString('fr-FR')}.pdf`);
        this.currentStateService.setReportGenerationInProgress(false);
      },
      error: async (error) => {
        const errorMessage = await this.extractErrorMessage(error);
        if (errorMessage) {
          this.fonctionsService.launch_toast(false, errorMessage);
        }
        this.currentStateService.setReportGenerationInProgress(false);
      }
    });
  }

  private downloadPDF(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private async extractErrorMessage(error: any): Promise<string | null> {
    if (error?.error instanceof Blob) {
      try {
        const text = await error.error.text();
        const jsonError = JSON.parse(text);
        return jsonError.message || null;
      } catch (e) {
        return null;
      }
    }
    return error?.error?.message || error?.message || null;
  }

  async deleteSeismograph(id: number) {
    const isDeleted = await this.seismographService.deleteSeismograph(id);
    if (isDeleted) {
      this.router.navigate(['/devices']);
      this.translate.get('SUCCESS.SEISMOGRAPH_DELETED').subscribe((translatedMessage: string) => {
        this.fonctionsService.launch_toast(true, translatedMessage);
      });
    } else {
      this.translate.get('ERRORS.EVENT_DELETE_ERROR').subscribe((translatedMessage: string) => {
        this.fonctionsService.launch_toast(false, translatedMessage);
      });
    }
  }


  public async onDelete() {
    const interpolationValues = {
      seismograph: this.device.serialNumber
    };

    this.translate.get([
      'MESSAGE_INFO.DELETE_SEISMOGRAPH',
      'MESSAGE_INFO.DELETE_SEISMOGRAPH_MESSAGE'
    ], interpolationValues).subscribe((translations: { [key: string]: string }) => {
      const translatedTitle = translations['MESSAGE_INFO.DELETE_SEISMOGRAPH'];
      const translatedMessage = translations['MESSAGE_INFO.DELETE_SEISMOGRAPH_MESSAGE'];

      const dialogRef = this.dialogConfirme.open(ConfirmDialogComponent, {
        enterAnimationDuration: '0ms',
        data: { title: translatedTitle, message: translatedMessage },
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.deleteSeismograph(this.device.id);
        }
      });
    });
  }

  async loadControlPoint(): Promise<void> {
    if (!this.device || !this.device.serialNumber) {
      return;
    }

    try {
      const controlPointInstrument =
        await this.seismographService.getControlPointInstrumentsBySeismographsId(
          [this.device.id]
        );

      if (controlPointInstrument.length > 0) {
        var ctrlPointId = controlPointInstrument[0].controlPointId;
        this.ctrlPoint = await this.controlPointService.getControlPointByIdAsync(ctrlPointId);
      }
    } catch (error) {
      console.error('Error loading device name:', error);
    }
    this.cdRef.detectChanges();
  }

  toggleCurrentCtrlPoint() {
    this.showCurrentCtrlPoint = !this.showCurrentCtrlPoint;
    if (!this.showCurrentCtrlPoint && this.isControlPointSelected) {
      this.isEditing = false;
      this.resetSelection();
    }
  }

  navigateToDetails(controlPoint: ControlPoint) {
    if (this.siteId && controlPoint.controlPointId) {
      this.router.navigate(['/'], {
        queryParams: {}
      });
      this.currentStateService.toggleSlideMenu(null, MENU_OPTIONS.CONTROL_POINT);
      this.currentStateService.setCurrentMenu(MENU_OPTIONS.CONTROL_POINT);
      setTimeout(() => {
        this.router.navigate(['/vibration-detail'], {
          queryParams: { siteId: this.siteId, controlPointId: controlPoint.controlPointId }
        });
      }, 1);
    }
  }

  onEditControlPoint(): void {
    this.isEditing = true;
    this.showCurrentCtrlPoint = true;
    if (this.ctrlPoint) {
      this.selectedControlPoint = this.ctrlPoint;
    }
    this.cdRef.detectChanges();
  }

  onControlPointSelect(cp: ControlPoint): void {
    this.selectedControlPoint = cp;
    this.isControlPointSelected = true;
    this.cdRef.detectChanges();
  }

  private async loadAvailableControlPointsForSite(siteId: number): Promise<void> {
    this.controlPointService.getControlPointsBySiteId(siteId, true).subscribe(async controlPoints => {
      const assignedControlPointIds = await this.getControlPointIdsAssignedToOtherDevices();
      this.controlPoints = controlPoints.filter(cp => !assignedControlPointIds.has(cp.controlPointId));
      this.optionsControlPoint = this.controlPoints.map(cp => ({
        id: cp.controlPointId,
        name: cp.name
      }));
      this.cdRef.detectChanges();
    });
  }

  private async getControlPointIdsAssignedToOtherDevices(): Promise<Set<number>> {
    const seismographs = await this.seismographService.getAllSeismographs();
    const seismographIds = seismographs
      .map(seismograph => seismograph.id)
      .filter(id => !!id);

    if (seismographIds.length === 0) {
      return new Set<number>();
    }

    const controlPointInstruments = await this.seismographService.getControlPointInstrumentsBySeismographsId(seismographIds);
    return new Set(
      controlPointInstruments
        .filter(instrument => instrument.seismographId !== this.device?.id)
        .map(instrument => instrument.controlPointId)
    );
  }

  async assignCpToDevice(): Promise<void> {
    if (!this.selectedControlPoint?.controlPointId || !this.device?.id) {
      this.translate.get('ERRORS.INVALID_CONTROL_POINT_SELECTION').subscribe((translatedMessage: string) => {
        this.fonctionsService.launch_toast(false, translatedMessage);
      });
      return;
    }

    try {
      this.isLoading = true;
      if (this.isEditing && this.ctrlPoint && this.ctrlPoint.controlPointId !== this.selectedControlPoint.controlPointId) {
        await this.seismographService.deleteControlPointInstrument(this.ctrlPoint.controlPointId, this.device.id);
      }

      await this.seismographService.createControlPointInstrument(
        this.selectedControlPoint.controlPointId,
        this.device.id,
        this.selectedControlPoint.name,
      );

      await this.loadControlPoint();
      this.translate
        .get(this.isEditing ? 'SUCCESS.CONTROL_POINT_UPDATED' : 'SUCCESS.CONTROL_POINT_ASSIGNED')
        .subscribe((translatedMessage: string) => {
          this.fonctionsService.launch_toast(true, translatedMessage);
        });
      this.isEditing = false;
      this.resetSelection();
    } catch (error) {
      const errorKey = this.isEditing ? 'ERRORS.CONTROL_POINT_UPDATE_ERROR' : 'ERRORS.CONTROL_POINT_ASSIGN_ERROR';
      this.translate.get(errorKey).subscribe((translatedMessage: string) => {
        this.fonctionsService.launch_toast(false, translatedMessage);
      });
    } finally {
      this.isLoading = false;
      this.cdRef.markForCheck();
    }
  }

  resetSelection(): void {
    this.selectedControlPoint = null;
    this.isControlPointSelected = false;
    this.cdRef.detectChanges();
  }

  onOptionCpSelected(controlPoint: any) {
    this.selectedControlPoint = this.controlPoints.find(cp => cp.controlPointId === controlPoint.id);
  }

  cancelModifyAssignedCp() {
    this.isEditing = false;
    if (this.ctrlPoint === null) {
      this.showCurrentCtrlPoint = false;
    }
    this.cdRef.detectChanges();
  }

  async unassignCp() {
    await this.seismographService.deleteControlPointInstrument(this.ctrlPoint.controlPointId, this.device.id);
  }

  confirmUnassign() {
    if (this.ctrlPoint && this.device) {
      const interpolationValues = {
        ctrlPoint: this.ctrlPoint.name,
        seismograph: this.device.serialNumber
      };

      this.translate.get([
        'MESSAGE_INFO.UNASSIGN_CONTROL_POINT',
        'MESSAGE_INFO.UNASSIGN_CONTROL_POINT_MESSAGE'
      ], interpolationValues).subscribe((translations: { [key: string]: string }) => {
        const translatedTitle = translations['MESSAGE_INFO.UNASSIGN_CONTROL_POINT'];
        const translatedMessage = translations['MESSAGE_INFO.UNASSIGN_CONTROL_POINT_MESSAGE'];

        const dialogRef = this.dialogConfirme.open(ConfirmDialogComponent, {
          enterAnimationDuration: '0ms',
          data: {
            title: translatedTitle,
            message: translatedMessage
          },
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.unassignCp();
            this.isEditing = false;
            this.ctrlPoint = null;
            this.cdRef.detectChanges();
          }
        });
      });
    }
  }

  onOptionSiteSelected(option: SelectOption | null): void {
    this.selectedControlPoint = null;
    
    if (option && option.id) {
      this.selectedSite = this.allSites.find(site => site.siteId === option.id) || null;
      
      if (this.selectedSite) {
        this.loadAvailableControlPointsForSite(this.selectedSite.siteId!);
      } else {
        this.controlPoints = [];
        this.optionsControlPoint = [];
        this.cdRef.detectChanges();
      }
    } else {
      this.selectedSite = null;
      this.controlPoints = [];
      this.optionsControlPoint = [];
      this.cdRef.detectChanges();
    }
  }

    getBatteryIcon(device: Seismograph): 'charging' | 'low' | 'medium' | 'battery' | 'full-battery' | 'normal' | 'unknown' {
    const level = device.batteryLevel;
    if (level == null) return 'unknown';
    const isConnected = this.getDeviceStatusInfo(device).isConnected;
    if (level > 100) {
      return isConnected ? 'charging' : 'normal';
    }
    if (level < 20) {
      return 'low';
    }
    if (level < 50) {
      return 'medium';
    }
    if (level < 80) {
      return 'battery';
    }
    if (level <= 100) {
      return 'full-battery';
    }

    return 'normal';
  }

  protected getDeviceStatusInfo(device: Seismograph): { isConnected: boolean, lastActive: string, RecMode?: string } {
    return {
      isConnected: !!device?.connected,
      lastActive: device.minutesAgo != null && device?.connected ? this.formatMinutesAgo(device.minutesAgo) : '--',
      RecMode: device.workingStatus || ''
    };
  }

  private formatMinutesAgo(minutesAgo: number): string {
    if (minutesAgo < 60) {
      return this.translate.instant('DEVICE_STATUS.MINUTES_AGO', { minutes: minutesAgo });
    }

    const hours = Math.floor(minutesAgo / 60);
    const minutes = minutesAgo % 60;

    return minutes > 0
      ? this.translate.instant(hours === 1 ? 'DEVICE_STATUS.HOUR_MINUTES_AGO' : 'DEVICE_STATUS.HOURS_MINUTES_AGO', { hours, minutes })
      : this.translate.instant(hours === 1 ? 'DEVICE_STATUS.HOUR_AGO' : 'DEVICE_STATUS.HOURS_AGO', { hours });
  }

  getStatusIcon(device: Seismograph): string {
    const { isConnected } = this.getDeviceStatusInfo(device);
    return isConnected ? './icon/active.svg' : './icon/red.svg';
  }

  getStatusTitle(device: Seismograph): string {
    const info = this.getDeviceStatusInfo(device);
    return info.isConnected ? info.lastActive + (info.RecMode ? ` - ${info.RecMode}` : '') : '--';
  }
  
  private async setCompanyNameForDevice(): Promise<void> {
    if (!this.device || !this.device.id) {
      this.companyName = '';
      return;
    }
    try {
      const [companyInstruments, companies] = await Promise.all([
        this.seismographService.getCompanyInstrumentsBySeismographIds([this.device.id]),
        this.seismographService.getAllCompanies()
      ]);
      const companyInstrument = companyInstruments.find((ci: any) => ci.seismographId === this.device.id);
      const companyId = companyInstrument?.companyId;
      const company = companies.find((c: any) => c.id === companyId);
      this.companyName = company ? company.name : '';
    } catch (error) {
      this.companyName = '';
    }
  }
}
